import { useUser } from "@clerk/expo";
import { useCallback, useEffect, useState } from "react";

import { useAxiosClient } from "@/hooks";
import type { UpdateStudyRequest } from "@/types/requests";
import type { Run, Study } from "@/types/resources";
import type {
  GetStudiesResponse,
  GetStudyRunResultsResponse,
} from "@/types/responses";

import { decodeRun } from "./decode";
import { waveFolders } from "./folders";
import { readCachedSamples, readSample, writeSample } from "./storage";
import { parseStudyName, TRACKER_PREFIX } from "./survey";
import type { RunSample } from "./types";

/* ── Types ─────────────────────────────────────── */

export type MidtermsData = ReturnType<typeof useMidtermsData>;

export interface TrackerStudy {
  /** A partisan-feed study, kept off the map. */
  feeds: boolean;
  lastRun: Run | null;
  state: string;
  studyId: string;
  wave: number;
}

/* ── Constants ─────────────────────────────────── */

/* Spacing that keeps us under the API rate limits:
   GET /studies 10/min, GET /studies/:id/runs/:runId 30/min,
   PUT /studies/:id 10/min. */
const LIST_DELAY_MS = 6500;
const MOVE_DELAY_MS = 6500;
const RESULTS_DELAY_MS = 2100;
const PAGE_SIZE = 20;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Latest wave per state whose last run succeeded. */
const latestCompleted = (studies: TrackerStudy[]): TrackerStudy[] => {
  const byState = new Map<string, TrackerStudy>();
  for (const study of studies) {
    if (study.feeds || study.lastRun?.conclusion !== "success") continue;
    const current = byState.get(study.state);
    if (!current || study.wave > current.wave) byState.set(study.state, study);
  }
  return [...byState.values()];
};

/* ── Hook ──────────────────────────────────────── */

/**
 * Finds every tracker study ("Midterms 2026 · XX · Wave N", and its
 * partisan-feed study "… · Partisan news"), then loads the latest completed
 * tracker run per state — from the browser cache when possible.
 * Samples stream in one state at a time so the map fills progressively.
 * Afterwards, the user's own tracker studies are filed in their wave folder.
 */
const useMidtermsData = () => {
  const { user } = useUser();
  const userId = user?.id;
  const [error, setError] = useState<string | null>(null);
  // True once the tracker studies have been listed: the next wave number
  // depends on them.
  const [listed, setListed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Paint what this browser already decoded while the API is queried.
  const [samples, setSamples] =
    useState<Record<string, RunSample>>(readCachedSamples);
  const [studies, setStudies] = useState<TrackerStudy[]>([]);

  const client = useAxiosClient("/studies");
  const folderClient = useAxiosClient("/folders");

  useEffect(() => {
    let cancelled = false;
    const own: { study: Study; wave: number }[] = [];

    const fileStudies = async () => {
      try {
        const folderFor = await waveFolders(folderClient);
        for (const { study, wave } of own) {
          if (cancelled) return;
          const folderId = await folderFor(wave);
          if (study.folder_id === folderId) continue;
          // Paced before the first move too: the previous load may have just
          // moved a study.
          await sleep(MOVE_DELAY_MS);
          if (cancelled) return;
          await client.put(`/${study.id}`, {
            description: study.description,
            folder_id: folderId,
            name: study.name,
            picture: study.picture,
            use_case: study.use_case,
          } satisfies UpdateStudyRequest);
        }
      } catch {
        // Best effort: the next load retries.
      }
    };

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const found: TrackerStudy[] = [];
        for (let page = 1; ; page++) {
          const { data } = await client.get<GetStudiesResponse>("", {
            params: { items_per_page: PAGE_SIZE, name: TRACKER_PREFIX, page },
          });
          for (const study of data.studies) {
            const parsed = parseStudyName(study.name);
            if (parsed) {
              found.push({
                feeds: parsed.feeds,
                lastRun: study.last_run,
                state: parsed.state,
                studyId: study.id,
                wave: parsed.wave,
              });
              if (study.created_by_id === userId) {
                own.push({ study, wave: parsed.wave });
              }
            }
          }
          if (
            data.studies.length < PAGE_SIZE ||
            page * PAGE_SIZE >= data.count
          ) {
            break;
          }
          await sleep(LIST_DELAY_MS);
          if (cancelled) return;
        }
        if (cancelled) return;
        setStudies(found);
        setListed(true);

        const targets = latestCompleted(found);
        const loaded: Record<string, RunSample> = {};
        setProgress({ done: 0, total: targets.length });
        for (const [i, study] of targets.entries()) {
          const runId = study.lastRun?.id;
          if (!runId) continue;
          let sample = readSample(runId);
          if (!sample) {
            // Results are paged by question (20 max); the questionnaire
            // fits in one page, but follow pages if it ever grows.
            const { data } = await client.get<GetStudyRunResultsResponse>(
              `/${study.studyId}/runs/${runId}`,
              { params: { items_per_page: PAGE_SIZE, page: 1 } }
            );
            for (let page = 2; (page - 1) * PAGE_SIZE < data.count; page++) {
              await sleep(RESULTS_DELAY_MS);
              const next = await client.get<GetStudyRunResultsResponse>(
                `/${study.studyId}/runs/${runId}`,
                { params: { items_per_page: PAGE_SIZE, page } }
              );
              data.results.push(...next.data.results);
              Object.assign(data.personas, next.data.personas);
            }
            sample = decodeRun(data, {
              state: study.state,
              studyId: study.studyId,
              wave: study.wave,
            });
            writeSample(sample);
            if (i < targets.length - 1) await sleep(RESULTS_DELAY_MS);
          }
          if (cancelled) return;
          const fresh = sample;
          loaded[study.state] = fresh;
          setSamples((current) => ({ ...current, [study.state]: fresh }));
          setProgress({ done: i + 1, total: targets.length });
        }
        // Drop cached states whose studies are gone (archived or renamed).
        setSamples(loaded);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) {
          setLoading(false);
          setProgress(null);
        }
      }
      if (!cancelled && own.length > 0) await fileStudies();
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [client, folderClient, reloadKey, userId]);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  return { error, listed, loading, progress, refresh, samples, studies };
};

export default useMidtermsData;
